import { DesignToken } from "@livechat/design-system-react-components";

import { ReportDistribution } from "constants/reports/distribution";
import { ticketDetailedSourcesLabel } from "helpers/tickets";
import { DetailedSource } from "interfaces/entities/ticket";
import { withVar } from "themes/helpers";

import { getFormatedPercent, getLabelsFromPeriod, getSeriesDataFromPeriod, sumValueFromPeriod } from "../helpers";
import { INewTicketsData, INewTicketsSeries, INewTicketsSourceMeta, INewTicketsSummary } from "../interfaces";

const sourceMap: Record<DetailedSource, INewTicketsSourceMeta> = {
  [DetailedSource.CreatedManually]: {
    color: withVar(DesignToken.DecorRed500),
  },
  [DetailedSource.Email]: {
    color: withVar(DesignToken.DecorYellow500),
  },
  [DetailedSource.ContactForm]: {
    color: withVar(DesignToken.DecorGreen300),
  },
  [DetailedSource.Api]: {
    color: withVar(DesignToken.DecorGray200),
  },
  [DetailedSource.Chat]: {
    color: withVar(DesignToken.DecorBlue500),
  },
  [DetailedSource.ContactWidget]: {
    color: withVar(DesignToken.DecorPurple500),
  },
  [DetailedSource.Webflow]: {
    color: withVar(DesignToken.DecorOrange500),
  },
  [DetailedSource.ChatBot]: {
    color: withVar(DesignToken.ProductsChatbot),
  },
  [DetailedSource.WordPress]: {
    color: withVar(DesignToken.DecorBlue200),
  },
  [DetailedSource.AiAgent]: {
    color: withVar(DesignToken.DecorPurple900),
  },
  [DetailedSource.Copilot]: {
    color: withVar(DesignToken.DecorBrown300),
  },
  [DetailedSource.Workflows]: {
    color: withVar(DesignToken.DecorGreen800),
  },
  [DetailedSource.WidgetTicketForm]: {
    color: withVar(DesignToken.DecorRed300),
  },
};

const summary = (
  source: DetailedSource,
  ticketSources: Record<DetailedSource, number>,
  overallTickets: number
): INewTicketsSummary => {
  const ticketsFromSource = sumValueFromPeriod(ticketSources, source);

  return {
    color: sourceMap[source].color,
    value: ticketsFromSource,
    label: `(${getFormatedPercent(ticketsFromSource / overallTickets, 2)})`,
    name: ticketDetailedSourcesLabel[source],
  };
};

const series = (
  source: DetailedSource,
  ticketSources: Record<DetailedSource, number>,
  distribution: ReportDistribution,
  allNewTicketsData: number[]
): INewTicketsSeries => {
  const chartData = getSeriesDataFromPeriod(ticketSources, source, distribution);

  return {
    name: ticketDetailedSourcesLabel[source],
    color: sourceMap[source].color,
    data: chartData,
    label: chartData.map((value, index) => `(${getFormatedPercent(value / allNewTicketsData[index], 2)})`),
  };
};

export function newTicketsDeserializer(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { data }: any,
  distribution: ReportDistribution,
  availableSources: DetailedSource[]
): INewTicketsData {
  const newTicketsData = getSeriesDataFromPeriod(data.newTickets, "newTickets", distribution);
  const newTicketsSummary = sumValueFromPeriod(data.newTickets, "newTickets");
  const newTicketsLabels = getLabelsFromPeriod(data.newTickets, distribution);

  const ticketSourcesLabels = getLabelsFromPeriod(data.ticketSources, distribution);

  const newTickets = {
    summary: [
      {
        value: newTicketsSummary,
        color: withVar(DesignToken.DecorGreen500),
      },
    ],
    labels: newTicketsLabels,
    series: [
      {
        name: "new tickets",
        color: withVar(DesignToken.DecorGreen500),
        data: newTicketsData,
      },
    ],
  };

  const ticketSources = {
    summary: availableSources.map(source => summary(source, data.ticketSources, newTicketsSummary)),
    labels: ticketSourcesLabels,
    series: availableSources.map(source => series(source, data.ticketSources, distribution, newTicketsData)),
  };

  return { newTickets, ticketSources };
}
